// pages/admin/AdminDashboard.js — Conecta Lagoa
// Painel administrativo (staff interno). Fase 1: Visão geral + Gestão de
// empresas. Sessão própria (admin_token/admin_user), não usa a AuthContext
// de candidato/empresa — ver AdminLogin.js.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'https://conectalagoa.onrender.com/api';

const V = {
  bg: '#0f1629', surface: '#161d33', surface2: '#1c2440', border: '#232b47',
  text: '#f4f6fb', muted: '#9099b8', muted2: '#6b7494',
  accent: '#1a3a8f', accent2: '#2d52c4', orange: '#e07b00',
  green: '#10b981', red: '#ef4444', amber: '#f59e0b',
};

const tok = () => localStorage.getItem('admin_token');
const hdr = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` });

const PLANOS = ['free', 'profissional', 'premium'];
const PLANO_LABEL = { free: 'Grátis', profissional: 'Profissional', premium: 'Premium' };

function fmtData(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
}

// "há 5 min" / "há 3h" / "há 2d" — usado no feed de atividades
function tempoRelativo(d) {
  if (!d) return '—';
  const diffMs = Date.now() - new Date(d).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'agora mesmo';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const dias = Math.floor(h / 24);
  return `há ${dias}d`;
}

const ATIVIDADE_META = {
  nova_empresa: { icone: '🏢', texto: (a) => `Nova empresa cadastrada: ${a.titulo}` },
  nova_vaga: { icone: '📋', texto: (a) => `Nova vaga publicada: ${a.titulo}${a.subtitulo ? ` — ${a.subtitulo}` : ''}` },
  scraper_executado: { icone: '🤖', texto: (a) => `Scraper executado: ${a.titulo}` },
  erro_scraper: { icone: '⚠️', texto: (a) => `Erro crítico do sistema: falha no scraper de ${a.titulo}` },
  curriculo_aprovado: { icone: '✅', texto: (a) => `Currículo aprovado: ${a.titulo}${a.subtitulo ? ` para ${a.subtitulo}` : ''}` },
  empresa_assinou_plano: { icone: '💳', texto: (a) => `${a.titulo} assinou o plano ${PLANO_LABEL[a.subtitulo] || a.subtitulo}` },
};

// ─── Card de métrica ────────────────────────────────────────────
function MetricCard({ label, value, hint }) {
  return (
    <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, padding: '16px 18px' }}>
      <div style={{ fontSize: 12, color: V.muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: V.text }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: V.muted2, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// ─── Cartão de gráfico ──────────────────────────────────────────
function ChartCard({ title, children }) {
  return (
    <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, padding: '16px 18px' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: V.text, marginBottom: 12 }}>{title}</div>
      <ResponsiveContainer width="100%" height={200}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

const chartAxisProps = {
  tick: { fill: V.muted, fontSize: 11 },
  axisLine: { stroke: V.border },
  tickLine: false,
};

const tooltipStyle = {
  contentStyle: { background: V.surface2, border: `1px solid ${V.border}`, borderRadius: 8, fontSize: 12 },
  labelStyle: { color: V.text },
  itemStyle: { color: V.text },
};

// ─── Visão geral ────────────────────────────────────────────────
function VisaoGeral() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/admin/dashboard`, { headers: hdr() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao buscar dashboard');
      setDados(data);
    } catch (err) {
      setErro(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading) return <div style={{ color: V.muted }}>Carregando...</div>;
  if (erro) return <div style={{ color: V.red }}>{erro}</div>;

  const { resumo, alertas, graficos, atividades } = dados;

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: V.text, marginBottom: 16 }}>Visão geral</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 28 }}>
        <MetricCard label="Vagas ativas" value={resumo.vagasAtivas} hint={`+${resumo.vagasNovasHoje} hoje`} />
        <MetricCard label="Vagas novas hoje" value={resumo.vagasNovasHoje} />
        <MetricCard label="Empresas cadastradas" value={resumo.empresasTotal} hint={`+${resumo.empresasNovasSemana} esta semana`} />
        <MetricCard label="Empresas ativas" value={resumo.empresasAtivas} />
        <MetricCard label="Candidatos cadastrados" value={resumo.candidatosTotal} hint={`+${resumo.candidatosNovosHoje} hoje`} />
        <MetricCard label="Candidatos ativos (30d)" value={resumo.candidatosAtivos30d} hint="perfil atualizado nos últimos 30 dias" />
        <MetricCard label="Candidaturas hoje" value={resumo.candidaturasHoje} />
        <MetricCard label="Contratações registradas" value={resumo.contratacoesTotal} hint={`+${resumo.contratacoesMes} este mês`} />
        <MetricCard label="Receita do mês" value={resumo.receitaMes != null ? resumo.receitaMes : '—'} hint="sem cobrança de planos ainda" />
        <MetricCard label="Taxa de conversão geral" value={resumo.taxaCandidatura} hint="candidaturas (30d) por vaga ativa" />
      </div>

      <h2 style={{ fontSize: 18, fontWeight: 700, color: V.text, marginBottom: 16 }}>Gráficos</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginBottom: 28 }}>
        <ChartCard title="Vagas publicadas por dia (14 dias)">
          <LineChart data={graficos.vagasPorDia}>
            <CartesianGrid stroke={V.border} vertical={false} />
            <XAxis dataKey="label" {...chartAxisProps} />
            <YAxis allowDecimals={false} {...chartAxisProps} width={28} />
            <Tooltip {...tooltipStyle} />
            <Line type="monotone" dataKey="n" stroke={V.accent2} strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>

        <ChartCard title="Candidaturas por dia (14 dias)">
          <LineChart data={graficos.candidaturasPorDia}>
            <CartesianGrid stroke={V.border} vertical={false} />
            <XAxis dataKey="label" {...chartAxisProps} />
            <YAxis allowDecimals={false} {...chartAxisProps} width={28} />
            <Tooltip {...tooltipStyle} />
            <Line type="monotone" dataKey="n" stroke={V.green} strokeWidth={2} dot={false} />
          </LineChart>
        </ChartCard>

        <ChartCard title="Novos candidatos por semana">
          <BarChart data={graficos.candidatosPorSemana}>
            <CartesianGrid stroke={V.border} vertical={false} />
            <XAxis dataKey="label" {...chartAxisProps} />
            <YAxis allowDecimals={false} {...chartAxisProps} width={28} />
            <Tooltip {...tooltipStyle} />
            <Bar dataKey="n" fill={V.accent2} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard title="Empresas cadastradas por mês">
          <BarChart data={graficos.empresasPorMes}>
            <CartesianGrid stroke={V.border} vertical={false} />
            <XAxis dataKey="label" {...chartAxisProps} />
            <YAxis allowDecimals={false} {...chartAxisProps} width={28} />
            <Tooltip {...tooltipStyle} />
            <Bar dataKey="n" fill={V.orange} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard title="Origem das vagas">
          <BarChart data={graficos.origemVagas} layout="vertical" margin={{ left: 8 }}>
            <CartesianGrid stroke={V.border} horizontal={false} />
            <XAxis type="number" allowDecimals={false} {...chartAxisProps} />
            <YAxis dataKey="label" type="category" {...chartAxisProps} width={90} />
            <Tooltip {...tooltipStyle} />
            <Bar dataKey="n" fill={V.accent2} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard title="Distribuição por cidade">
          <BarChart data={graficos.vagasPorCidade} layout="vertical" margin={{ left: 8 }}>
            <CartesianGrid stroke={V.border} horizontal={false} />
            <XAxis type="number" allowDecimals={false} {...chartAxisProps} />
            <YAxis dataKey="label" type="category" {...chartAxisProps} width={90} />
            <Tooltip {...tooltipStyle} />
            <Bar dataKey="n" fill={V.green} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ChartCard>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1.3fr) minmax(240px, 1fr)', gap: 24, alignItems: 'start', marginBottom: 28 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: V.text, marginBottom: 16 }}>Feed de atividades</h2>
          {(!atividades || atividades.length === 0) ? (
            <div style={{ color: V.muted, fontSize: 13 }}>Nenhuma atividade recente.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {atividades.map((a, i) => {
                const meta = ATIVIDADE_META[a.tipo] || { icone: '•', texto: () => a.titulo };
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: V.surface, border: `1px solid ${V.border}`, borderRadius: 10, padding: '10px 14px' }}>
                    <span style={{ fontSize: 15 }}>{meta.icone}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: V.text, fontSize: 13 }}>{meta.texto(a)}</div>
                      <div style={{ color: V.muted2, fontSize: 11, marginTop: 2 }}>{tempoRelativo(a.quando)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: V.text, marginBottom: 16 }}>Alertas</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <AlertaItem ativo={alertas.vagasExpirandoHoje > 0} texto={`${alertas.vagasExpirandoHoje} vaga(s) expirando hoje`} />
            <AlertaItem ativo={alertas.fontesComErro > 0} texto={`${alertas.fontesComErro} fonte(s) de scraping com erro`} />
            <AlertaItem ativo={alertas.empresasSemAcesso30d > 0} texto={`${alertas.empresasSemAcesso30d} empresa(s) sem acesso há 30 dias`} />
            <AlertaItem ativo={false} texto="Pagamentos pendentes" hint="sem cobrança de planos ainda" />
            <AlertaItem ativo={false} texto="Revisões aguardando aprovação" hint="fila de revisão ainda não implementada" />
          </div>
        </div>
      </div>

      <AcoesRapidas onRecarregar={carregar} />
    </div>
  );
}

// ─── Item de alerta ─────────────────────────────────────────────
function AlertaItem({ ativo, texto, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: V.surface, border: `1px solid ${V.border}`, borderRadius: 10, padding: '12px 16px' }}>
      <span style={{ background: ativo ? '#4a1f1f' : V.surface2, color: ativo ? '#f0a0a0' : V.muted, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
        {ativo ? 'Atenção' : 'OK'}
      </span>
      <span style={{ color: V.text, fontSize: 13 }}>{texto}</span>
      {hint && <span style={{ color: V.muted2, fontSize: 11 }}>({hint})</span>}
    </div>
  );
}

// ─── Ações rápidas ──────────────────────────────────────────────
function AcoesRapidas({ onRecarregar }) {
  const [modal, setModal] = useState(null); // null | 'vaga' | 'empresa'
  const [rodandoScraper, setRodandoScraper] = useState(false);
  const [toast, setToast] = useState('');

  const executarScraper = async () => {
    setRodandoScraper(true);
    setToast('');
    try {
      const res = await fetch(`${API_URL}/admin/scraper/executar`, { method: 'POST', headers: hdr() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao rodar scraper');
      setToast(
        `Scraper executado: ${data.fontesVerificadas} fonte(s) verificada(s), ` +
        `${data.vagasNovas} vaga(s) nova(s), ${data.duplicadas} duplicada(s)` +
        (data.erros?.length ? `, ${data.erros.length} erro(s)` : ''),
      );
      onRecarregar();
    } catch (err) {
      setToast(err.message);
    } finally {
      setRodandoScraper(false);
    }
  };

  const botao = (label, onClick, disabled) => (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: '10px 16px', borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface, color: V.text, fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1 }}>
      {label}
    </button>
  );

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: V.text, marginBottom: 16 }}>Ações rápidas</h2>
      {toast && <div style={{ background: V.surface2, color: V.text, fontSize: 12, borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>{toast}</div>}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {botao('+ Criar vaga', () => setModal('vaga'))}
        {botao('+ Criar empresa', () => setModal('empresa'))}
        {botao(rodandoScraper ? 'Executando scraper...' : 'Executar scraper', executarScraper, rodandoScraper)}
      </div>

      {modal === 'vaga' && (
        <ModalCriarVaga onClose={() => setModal(null)} onCriada={() => { setModal(null); onRecarregar(); }} />
      )}
      {modal === 'empresa' && (
        <ModalCriarEmpresa onClose={() => setModal(null)} onCriada={() => { setModal(null); onRecarregar(); }} />
      )}
    </div>
  );
}

// ─── Modal: criar vaga (admin, em nome de uma empresa) ──────────
function ModalCriarVaga({ onClose, onCriada }) {
  const [empresas, setEmpresas] = useState([]);
  const [modoEmpresa, setModoEmpresa] = useState('cadastrada'); // 'cadastrada' | 'sem_conta'
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState({
    empresa_id: '', empresa_nome: '', titulo: '', descricao: '', cidade: '', estado: '',
    tipo_contrato: 'CLT', modalidade: 'Presencial',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/admin/empresas`, { headers: hdr() });
        const data = await res.json();
        setEmpresas(data.empresas || []);
      } catch {
        setEmpresas([]);
      }
    })();
  }, []);

  const salvar = async () => {
    if (!form.titulo) { setErro('Informe o título da vaga'); return; }
    if (modoEmpresa === 'cadastrada' && !form.empresa_id) { setErro('Selecione a empresa'); return; }
    if (modoEmpresa === 'sem_conta' && !form.empresa_nome.trim()) { setErro('Informe o nome da empresa'); return; }

    setSalvando(true);
    setErro('');
    try {
      const body = modoEmpresa === 'cadastrada'
        ? { ...form, empresa_nome: undefined }
        : { ...form, empresa_id: undefined };
      const res = await fetch(`${API_URL}/admin/vagas`, { method: 'POST', headers: hdr(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || 'Erro ao criar vaga');
      onCriada();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', marginBottom: 10, borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13 };

  const tab = (key, label) => (
    <button onClick={() => setModoEmpresa(key)}
      style={{
        flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        border: `1px solid ${modoEmpresa === key ? V.accent2 : V.border}`,
        background: modoEmpresa === key ? V.accent : 'transparent',
        color: modoEmpresa === key ? 'white' : V.muted,
      }}>
      {label}
    </button>
  );

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, padding: 28, width: 460, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: V.text }}>Criar vaga</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: V.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        {erro && <div style={{ background: '#3a1a1a', border: '1px solid #7a2e2e', color: '#f0a0a0', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{erro}</div>}

        <label style={{ fontSize: 11, color: V.muted, display: 'block', marginBottom: 4 }}>Empresa</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {tab('cadastrada', 'Já cadastrada')}
          {tab('sem_conta', 'Ainda sem conta')}
        </div>

        {modoEmpresa === 'cadastrada' ? (
          <select value={form.empresa_id} onChange={(e) => setForm({ ...form, empresa_id: e.target.value })} style={inputStyle}>
            <option value="">Selecione...</option>
            {empresas.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
          </select>
        ) : (
          <>
            <input placeholder="Nome da empresa" value={form.empresa_nome} onChange={(e) => setForm({ ...form, empresa_nome: e.target.value })} style={inputStyle} />
            <div style={{ fontSize: 11, color: V.muted2, marginTop: -6, marginBottom: 10 }}>
              A vaga fica no site sem empresa vinculada; se a empresa quiser, pode reivindicá-la depois criando uma conta.
            </div>
          </>
        )}

        <input placeholder="Título da vaga" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} style={inputStyle} />
        <textarea placeholder="Descrição" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} rows={3} style={inputStyle} />
        <div style={{ display: 'flex', gap: 8 }}>
          <input placeholder="Cidade" value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} style={inputStyle} />
          <input placeholder="UF" maxLength={2} value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase() })} style={{ ...inputStyle, width: 70 }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={form.tipo_contrato} onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })} style={inputStyle}>
            {['CLT', 'PJ', 'Estágio', 'Temporário', 'Freelance'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={form.modalidade} onChange={(e) => setForm({ ...form, modalidade: e.target.value })} style={inputStyle}>
            {['Presencial', 'Remoto', 'Híbrido'].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <button onClick={salvar} disabled={salvando}
          style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {salvando ? 'Criando...' : 'Criar vaga'}
        </button>
      </div>
    </div>
  );
}

// ─── Modal: criar empresa (cadastro manual pelo admin) ──────────
function ModalCriarEmpresa({ onClose, onCriada }) {
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [senhaGerada, setSenhaGerada] = useState('');
  const [form, setForm] = useState({ nome: '', email: '', cnpj: '', telefone: '', cidade: '', estado: '' });

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', marginBottom: 10, borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13 };

  const salvar = async () => {
    if (!form.nome || !form.email) { setErro('Nome e e-mail são obrigatórios'); return; }
    setSalvando(true);
    setErro('');
    try {
      const res = await fetch(`${API_URL}/admin/empresas`, { method: 'POST', headers: hdr(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao cadastrar empresa');
      if (data.senhaGerada) {
        setSenhaGerada(data.senhaGerada);
      } else {
        onCriada();
      }
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, padding: 28, width: 420, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: V.text }}>Criar empresa</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: V.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        {erro && <div style={{ background: '#3a1a1a', border: '1px solid #7a2e2e', color: '#f0a0a0', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{erro}</div>}

        {senhaGerada ? (
          <>
            <div style={{ background: V.surface2, borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: V.text }}>
              Empresa criada. Senha temporária gerada — repasse pro contato da empresa:
              <div style={{ fontFamily: 'monospace', fontSize: 15, color: V.orange, marginTop: 8 }}>{senhaGerada}</div>
            </div>
            <button onClick={onCriada}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Concluir
            </button>
          </>
        ) : (
          <>
            <input placeholder="Nome da empresa" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} style={inputStyle} />
            <input placeholder="E-mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} />
            <input placeholder="CNPJ (opcional)" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} style={inputStyle} />
            <input placeholder="Telefone" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} style={inputStyle} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input placeholder="Cidade" value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} style={inputStyle} />
              <input placeholder="UF" maxLength={2} value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase() })} style={{ ...inputStyle, width: 70 }} />
            </div>
            <button onClick={salvar} disabled={salvando}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {salvando ? 'Criando...' : 'Criar empresa'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Detalhe / edição de uma empresa ────────────────────────────
function PainelEmpresa({ empresaId, onClose, onChanged }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [toast, setToast] = useState('');
  const [form, setForm] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/empresas/${empresaId}`, { headers: hdr() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setDados(data);
      setForm({
        nome: data.empresa.nome || '',
        cidade: data.empresa.cidade || '',
        estado: data.empresa.estado || '',
        telefone: data.empresa.telefone || '',
      });
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvarDados = async () => {
    setSalvando(true);
    try {
      const res = await fetch(`${API_URL}/admin/empresas/${empresaId}`, {
        method: 'PUT', headers: hdr(), body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setToast('Dados salvos');
      onChanged();
    } catch (err) {
      setToast(err.message);
    } finally {
      setSalvando(false);
    }
  };

  const mudarPlano = async (plano) => {
    try {
      const res = await fetch(`${API_URL}/admin/empresas/${empresaId}/plano`, {
        method: 'PUT', headers: hdr(), body: JSON.stringify({ plano }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setToast(`Plano alterado para ${PLANO_LABEL[plano]}`);
      carregar();
      onChanged();
    } catch (err) {
      setToast(err.message);
    }
  };

  const alternarStatus = async () => {
    const novoAtivo = !dados.empresa.ativo;
    try {
      const res = await fetch(`${API_URL}/admin/empresas/${empresaId}/status`, {
        method: 'PUT', headers: hdr(), body: JSON.stringify({ ativo: novoAtivo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setToast(novoAtivo ? 'Empresa reativada' : 'Empresa suspensa');
      carregar();
      onChanged();
    } catch (err) {
      setToast(err.message);
    }
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, padding: 28, width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        {loading || !dados || !form ? (
          <div style={{ color: V.muted }}>Carregando...</div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: V.text }}>{dados.empresa.nome}</div>
                <div style={{ fontSize: 12, color: V.muted }}>{dados.empresa.email}</div>
              </div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: V.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>

            {toast && <div style={{ background: V.surface2, color: V.text, fontSize: 12, borderRadius: 8, padding: '8px 12px', marginBottom: 16 }}>{toast}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 20 }}>
              <MetricCard label="Vagas ativas / total" value={`${dados.metricas.vagasAtivas} / ${dados.metricas.vagasTotal}`} />
              <MetricCard label="Candidaturas recebidas" value={dados.metricas.candidaturasRecebidas} />
              <MetricCard label="Currículos visualizados" value={dados.metricas.curriculosVisualizados} />
              <MetricCard label="Tempo médio p/ aprovar" value={dados.metricas.tempoMedioParaAprovarDias != null ? `${dados.metricas.tempoMedioParaAprovarDias}d` : '—'} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: V.muted, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Plano</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {PLANOS.map((p) => (
                  <button key={p} onClick={() => mudarPlano(p)}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${dados.empresa.plano === p ? V.accent2 : V.border}`,
                      background: dados.empresa.plano === p ? V.accent : 'transparent',
                      color: dados.empresa.plano === p ? 'white' : V.muted,
                    }}>
                    {PLANO_LABEL[p]}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: V.muted, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Dados</label>
              {['nome', 'cidade', 'estado', 'telefone'].map((campo) => (
                <input key={campo} value={form[campo]} onChange={(e) => setForm({ ...form, [campo]: e.target.value })}
                  placeholder={campo}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', marginBottom: 8, borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13 }} />
              ))}
              <button onClick={salvarDados} disabled={salvando}
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {salvando ? 'Salvando...' : 'Salvar dados'}
              </button>
            </div>

            <button onClick={alternarStatus}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${dados.empresa.ativo ? V.red : V.green}`, background: 'transparent', color: dados.empresa.ativo ? V.red : V.green, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {dados.empresa.ativo ? 'Suspender empresa' : 'Reativar empresa'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Gestão de empresas ─────────────────────────────────────────
function GestaoEmpresas() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [empresaAberta, setEmpresaAberta] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const qs = busca ? `?busca=${encodeURIComponent(busca)}` : '';
      const res = await fetch(`${API_URL}/admin/empresas${qs}`, { headers: hdr() });
      const data = await res.json();
      setEmpresas(data.empresas || []);
    } catch {
      setEmpresas([]);
    } finally {
      setLoading(false);
    }
  }, [busca]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: V.text, margin: 0 }}>Gestão de empresas</h2>
        <input
          value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome ou e-mail..."
          style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13, width: 240 }}
        />
      </div>

      {loading ? (
        <div style={{ color: V.muted }}>Carregando...</div>
      ) : empresas.length === 0 ? (
        <div style={{ color: V.muted }}>Nenhuma empresa encontrada.</div>
      ) : (
        <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: V.muted, fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ padding: '12px 16px' }}>Empresa</th>
                <th style={{ padding: '12px 16px' }}>Plano</th>
                <th style={{ padding: '12px 16px' }}>Vagas</th>
                <th style={{ padding: '12px 16px' }}>Último acesso</th>
                <th style={{ padding: '12px 16px' }}>Status</th>
                <th style={{ padding: '12px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {empresas.map((e) => (
                <tr key={e.id} style={{ borderTop: `1px solid ${V.border}` }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ color: V.text, fontWeight: 600 }}>{e.nome}</div>
                    <div style={{ color: V.muted2, fontSize: 11 }}>{e.email}</div>
                  </td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>{e.planoNome}</td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>{e.vagas_ativas} / {e.vagas_total}</td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>{fmtData(e.ultimo_acesso)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: e.ativo ? '#123a2a' : '#4a1f1f', color: e.ativo ? '#6ee7b7' : '#f0a0a0' }}>
                      {e.ativo ? 'Ativa' : 'Suspensa'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button onClick={() => setEmpresaAberta(e.id)}
                      style={{ background: 'none', border: `1px solid ${V.border}`, color: V.text, padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                      Gerenciar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {empresaAberta && (
        <PainelEmpresa empresaId={empresaAberta} onClose={() => setEmpresaAberta(null)} onChanged={carregar} />
      )}
    </div>
  );
}

// ─── Monitoramento de scrapers (Painel dos robôs) ───────────────
function PainelScrapers() {
  const [fontes, setFontes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [rodando, setRodando] = useState(false);
  const [modalNova, setModalNova] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/scrapers`, { headers: hdr() });
      const data = await res.json();
      setFontes(data.fontes || []);
    } catch {
      setFontes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const executarAgora = async () => {
    setRodando(true);
    setToast('');
    try {
      const res = await fetch(`${API_URL}/admin/scraper/executar`, { method: 'POST', headers: hdr() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao rodar scraper');
      setToast(`${data.fontesVerificadas} fonte(s) verificada(s), ${data.vagasNovas} vaga(s) nova(s), ${data.duplicadas} duplicada(s)`);
      carregar();
    } catch (err) {
      setToast(err.message);
    } finally {
      setRodando(false);
    }
  };

  const alternarFonte = async (id, ativo) => {
    try {
      await fetch(`${API_URL}/admin/scrapers/${id}`, { method: 'PATCH', headers: hdr(), body: JSON.stringify({ ativo }) });
      carregar();
    } catch { /* noop */ }
  };

  const statusBadge = (fonte) => {
    if (!fonte.ativo) return { label: 'Desativada', bg: V.surface2, color: V.muted };
    if (!fonte.ultima_verificacao) return { label: 'Nunca rodou', bg: V.surface2, color: V.muted };
    if ((fonte.ultimo_status || '').toLowerCase().includes('erro')) return { label: 'Erro', bg: '#4a1f1f', color: '#f0a0a0' };
    return { label: 'OK', bg: '#123a2a', color: '#6ee7b7' };
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: V.text, margin: 0 }}>Monitoramento de scrapers</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setModalNova(true)}
            style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${V.border}`, background: 'none', color: V.text, fontSize: 12, cursor: 'pointer' }}>
            + Nova fonte
          </button>
          <button onClick={executarAgora} disabled={rodando}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 12, fontWeight: 600, cursor: rodando ? 'default' : 'pointer', opacity: rodando ? 0.6 : 1 }}>
            {rodando ? 'Executando...' : 'Executar agora'}
          </button>
        </div>
      </div>

      {toast && <div style={{ background: V.surface2, color: V.text, fontSize: 12, borderRadius: 8, padding: '8px 12px', marginBottom: 16 }}>{toast}</div>}

      {loading ? (
        <div style={{ color: V.muted }}>Carregando...</div>
      ) : fontes.length === 0 ? (
        <div style={{ color: V.muted }}>Nenhuma fonte cadastrada.</div>
      ) : (
        <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: V.muted, fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ padding: '12px 16px' }}>Fonte</th>
                <th style={{ padding: '12px 16px' }}>Última execução</th>
                <th style={{ padding: '12px 16px' }}>Vagas encontradas</th>
                <th style={{ padding: '12px 16px' }}>Status</th>
                <th style={{ padding: '12px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {fontes.map((f) => {
                const badge = statusBadge(f);
                return (
                  <tr key={f.id} style={{ borderTop: `1px solid ${V.border}` }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ color: V.text, fontWeight: 600 }}>{f.empresa_nome}</div>
                      <div style={{ color: V.muted2, fontSize: 11, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.url}</div>
                    </td>
                    <td style={{ padding: '12px 16px', color: V.muted }}>{f.ultima_verificacao ? tempoRelativo(f.ultima_verificacao) : '—'}</td>
                    <td style={{ padding: '12px 16px', color: V.muted }}>{f.vagas_encontradas ?? 0}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: badge.bg, color: badge.color }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => alternarFonte(f.id, !f.ativo)}
                        style={{ background: 'none', border: `1px solid ${V.border}`, color: V.text, padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                        {f.ativo ? 'Desativar' : 'Ativar'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalNova && <ModalNovaFonte onClose={() => setModalNova(false)} onCriada={() => { setModalNova(false); carregar(); }} />}
    </div>
  );
}

function ModalNovaFonte({ onClose, onCriada }) {
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState({ empresa_nome: '', url: '', categoria: '', cidade: '', estado: '' });
  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', marginBottom: 10, borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13 };

  const salvar = async () => {
    if (!form.empresa_nome || !form.url) { setErro('Nome da empresa e URL são obrigatórios'); return; }
    setSalvando(true);
    setErro('');
    try {
      const res = await fetch(`${API_URL}/admin/scrapers`, { method: 'POST', headers: hdr(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao adicionar fonte');
      onCriada();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, padding: 28, width: 420, maxWidth: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: V.text }}>Nova fonte de scraping</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: V.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {erro && <div style={{ background: '#3a1a1a', border: '1px solid #7a2e2e', color: '#f0a0a0', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{erro}</div>}
        <input placeholder="Nome da empresa" value={form.empresa_nome} onChange={(e) => setForm({ ...form, empresa_nome: e.target.value })} style={inputStyle} />
        <input placeholder="URL da página de vagas" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} style={inputStyle} />
        <input placeholder="Categoria (opcional)" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} style={inputStyle} />
        <div style={{ display: 'flex', gap: 8 }}>
          <input placeholder="Cidade" value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} style={inputStyle} />
          <input placeholder="UF" maxLength={2} value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase() })} style={{ ...inputStyle, width: 70 }} />
        </div>
        <button onClick={salvar} disabled={salvando}
          style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {salvando ? 'Adicionando...' : 'Adicionar fonte'}
        </button>
      </div>
    </div>
  );
}

// ─── Gestão de vagas ─────────────────────────────────────────────
function GestaoVagas() {
  const [subaba, setSubaba] = useState('site'); // 'site' | 'externas'

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setSubaba('site')}
          style={{
            padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${subaba === 'site' ? V.accent2 : V.border}`,
            background: subaba === 'site' ? V.accent : 'transparent',
            color: subaba === 'site' ? 'white' : V.muted,
          }}>
          Vagas do site
        </button>
        <button onClick={() => setSubaba('externas')}
          style={{
            padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${subaba === 'externas' ? V.accent2 : V.border}`,
            background: subaba === 'externas' ? V.accent : 'transparent',
            color: subaba === 'externas' ? 'white' : V.muted,
          }}>
          Vagas externas coletadas
        </button>
      </div>
      {subaba === 'site' ? <VagasDoSite /> : <VagasExternas />}
    </div>
  );
}

function VagasDoSite() {
  const [vagas, setVagas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [origem, setOrigem] = useState('');
  const [modalCriar, setModalCriar] = useState(false);
  const [modalImportar, setModalImportar] = useState(false);
  const [editando, setEditando] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (busca) params.set('busca', busca);
      if (origem) params.set('origem', origem);
      const res = await fetch(`${API_URL}/admin/vagas?${params}`, { headers: hdr() });
      const data = await res.json();
      setVagas(data.vagas || []);
    } catch {
      setVagas([]);
    } finally {
      setLoading(false);
    }
  }, [busca, origem]);

  useEffect(() => { carregar(); }, [carregar]);

  const alternarStatus = async (id, ativa) => {
    try {
      await fetch(`${API_URL}/admin/vagas/${id}/status`, { method: 'PATCH', headers: hdr(), body: JSON.stringify({ ativa }) });
      carregar();
    } catch { /* noop */ }
  };

  const ORIGEM_LABEL = {
    manual: 'Manual', scraper: 'Scraper', reivindicada: 'Reivindicada',
    reivindicada_externa: 'Reivindicada (externa)', manual_sem_conta: 'Manual (sem conta)',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: V.text, margin: 0 }}>Vagas do site</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar título ou empresa..."
            style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13, width: 220 }} />
          <select value={origem} onChange={(e) => setOrigem(e.target.value)}
            style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13 }}>
            <option value="">Todas as origens</option>
            {Object.entries(ORIGEM_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={() => setModalImportar(true)}
            style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${V.border}`, background: 'none', color: V.text, fontSize: 12, cursor: 'pointer' }}>
            Importar CSV
          </button>
          <button onClick={() => setModalCriar(true)}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            + Criar vaga
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: V.muted }}>Carregando...</div>
      ) : vagas.length === 0 ? (
        <div style={{ color: V.muted }}>Nenhuma vaga encontrada.</div>
      ) : (
        <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: V.muted, fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ padding: '12px 16px' }}>Vaga</th>
                <th style={{ padding: '12px 16px' }}>Empresa</th>
                <th style={{ padding: '12px 16px' }}>Origem</th>
                <th style={{ padding: '12px 16px' }}>Candidaturas</th>
                <th style={{ padding: '12px 16px' }}>Publicada</th>
                <th style={{ padding: '12px 16px' }}>Status</th>
                <th style={{ padding: '12px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {vagas.map((v) => (
                <tr key={v.id} style={{ borderTop: `1px solid ${V.border}` }}>
                  <td style={{ padding: '12px 16px', color: V.text, fontWeight: 600 }}>{v.titulo}</td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>
                    {v.empresa_nome}
                    {v.sem_conta && <span style={{ marginLeft: 6, fontSize: 10, color: V.orange }}>(sem conta)</span>}
                  </td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>{ORIGEM_LABEL[v.origem] || v.origem}</td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>{v.candidaturas}</td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>{fmtData(v.created_at)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: v.ativa ? '#123a2a' : V.surface2, color: v.ativa ? '#6ee7b7' : V.muted }}>
                      {v.ativa ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditando(v.id)}
                      style={{ background: 'none', border: `1px solid ${V.border}`, color: V.text, padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                      Editar
                    </button>
                    <button onClick={() => alternarStatus(v.id, !v.ativa)}
                      style={{ background: 'none', border: `1px solid ${V.border}`, color: V.text, padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                      {v.ativa ? 'Desativar' : 'Ativar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalCriar && <ModalCriarVaga onClose={() => setModalCriar(false)} onCriada={() => { setModalCriar(false); carregar(); }} />}
      {modalImportar && <ModalImportarVagasCsv onClose={() => setModalImportar(false)} onImportado={() => { setModalImportar(false); carregar(); }} />}
      {editando && <ModalEditarVaga vagaId={editando} onClose={() => setEditando(null)} onSalvo={() => { setEditando(null); carregar(); }} />}
    </div>
  );
}

// ─── Modal: editar vaga (qualquer origem — manual, scraper, reivindicada) ──
function ModalEditarVaga({ vagaId, onClose, onSalvo }) {
  const [form, setForm] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/admin/vagas/${vagaId}`, { headers: hdr() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        const v = data.vaga;
        setForm({
          titulo: v.titulo || '', descricao: v.descricao || '', requisitos: v.requisitos || '',
          salario: v.salario || '', cidade: v.cidade || '', estado: v.estado || '',
          area: v.area || '', tipo_contrato: v.tipo_contrato || 'CLT', modalidade: v.modalidade || 'Presencial',
          empresa_nome_externa: v.empresa_nome_externa || '', semConta: !!v.empresa_nome_externa,
        });
      } catch (err) {
        setErro(err.message);
      } finally {
        setCarregando(false);
      }
    })();
  }, [vagaId]);

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      const { semConta, ...body } = form;
      if (!semConta) delete body.empresa_nome_externa;
      const res = await fetch(`${API_URL}/admin/vagas/${vagaId}`, { method: 'PATCH', headers: hdr(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || 'Erro ao salvar');
      onSalvo();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', marginBottom: 10, borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13 };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, padding: 28, width: 480, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: V.text }}>Editar vaga</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: V.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        {erro && <div style={{ background: '#3a1a1a', border: '1px solid #7a2e2e', color: '#f0a0a0', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{erro}</div>}

        {carregando || !form ? (
          <div style={{ color: V.muted }}>Carregando...</div>
        ) : (
          <>
            {form.semConta && (
              <>
                <label style={{ fontSize: 11, color: V.muted, display: 'block', marginBottom: 4 }}>Empresa (sem conta no sistema)</label>
                <input value={form.empresa_nome_externa} onChange={(e) => setForm({ ...form, empresa_nome_externa: e.target.value })} style={inputStyle} />
              </>
            )}
            <input placeholder="Título da vaga" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} style={inputStyle} />
            <textarea placeholder="Descrição" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} rows={4} style={inputStyle} />
            <textarea placeholder="Requisitos" value={form.requisitos} onChange={(e) => setForm({ ...form, requisitos: e.target.value })} rows={2} style={inputStyle} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input placeholder="Cidade" value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} style={inputStyle} />
              <input placeholder="UF" maxLength={2} value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase() })} style={{ ...inputStyle, width: 70 }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={form.tipo_contrato} onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })} style={inputStyle}>
                {['CLT', 'PJ', 'Estágio', 'Temporário', 'Freelance'].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={form.modalidade} onChange={(e) => setForm({ ...form, modalidade: e.target.value })} style={inputStyle}>
                {['Presencial', 'Remoto', 'Híbrido'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <input placeholder="Área (opcional)" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} style={inputStyle} />
            <input placeholder="Salário (opcional)" value={form.salario} onChange={(e) => setForm({ ...form, salario: e.target.value })} style={inputStyle} />

            <button onClick={salvar} disabled={salvando}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {salvando ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Vagas externas coletadas (tabela "jobs", agente Python) ────
function VagasExternas() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (busca) params.set('busca', busca);
      const res = await fetch(`${API_URL}/admin/jobs?${params}`, { headers: hdr() });
      const data = await res.json();
      setJobs(data.jobs || []);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [busca]);

  useEffect(() => { carregar(); }, [carregar]);

  const alternarAtivo = async (id, active) => {
    try {
      await fetch(`${API_URL}/admin/jobs/${id}`, { method: 'PATCH', headers: hdr(), body: JSON.stringify({ active }) });
      carregar();
    } catch { /* noop */ }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 12, color: V.muted2, maxWidth: 420 }}>
          Coletadas pelo agente de scraping (Jobsora, JobConvo etc). Ficam sem candidatura/matching até a empresa reivindicar — mas você pode corrigir os dados aqui.
        </div>
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar título ou empresa..."
          style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13, width: 220 }} />
      </div>

      {loading ? (
        <div style={{ color: V.muted }}>Carregando...</div>
      ) : jobs.length === 0 ? (
        <div style={{ color: V.muted }}>Nenhuma vaga externa coletada ainda.</div>
      ) : (
        <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: V.muted, fontSize: 11, textTransform: 'uppercase' }}>
                <th style={{ padding: '12px 16px' }}>Vaga</th>
                <th style={{ padding: '12px 16px' }}>Empresa</th>
                <th style={{ padding: '12px 16px' }}>Fonte</th>
                <th style={{ padding: '12px 16px' }}>Coletada</th>
                <th style={{ padding: '12px 16px' }}>Status</th>
                <th style={{ padding: '12px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} style={{ borderTop: `1px solid ${V.border}` }}>
                  <td style={{ padding: '12px 16px', color: V.text, fontWeight: 600 }}>{j.title}</td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>{j.company}</td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>{j.source}</td>
                  <td style={{ padding: '12px 16px', color: V.muted }}>{fmtData(j.collected_at)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    {j.reivindicada_por_empresa_id ? (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: V.surface2, color: V.muted }}>Reivindicada</span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: j.active ? '#123a2a' : V.surface2, color: j.active ? '#6ee7b7' : V.muted }}>
                        {j.active ? 'Ativa' : 'Inativa'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditando(j.id)}
                      style={{ background: 'none', border: `1px solid ${V.border}`, color: V.text, padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                      Editar
                    </button>
                    {!j.reivindicada_por_empresa_id && (
                      <button onClick={() => alternarAtivo(j.id, !j.active)}
                        style={{ background: 'none', border: `1px solid ${V.border}`, color: V.text, padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                        {j.active ? 'Desativar' : 'Ativar'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editando && <ModalEditarJobExterno jobId={editando} onClose={() => setEditando(null)} onSalvo={() => { setEditando(null); carregar(); }} />}
    </div>
  );
}

function ModalEditarJobExterno({ jobId, onClose, onSalvo }) {
  const [form, setForm] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    (async () => {
      try {
        // Não existe GET individual — reaproveita a listagem e filtra localmente.
        const res = await fetch(`${API_URL}/admin/jobs`, { headers: hdr() });
        const data = await res.json();
        const j = (data.jobs || []).find((x) => String(x.id) === String(jobId));
        if (!j) throw new Error('Vaga não encontrada');
        setForm({
          title: j.title || '', company: j.company || '', city: j.city || '', state: j.state || '',
          remote: !!j.remote, salary: j.salary || '', description: j.description || '', source_url: j.source_url || '',
        });
      } catch (err) {
        setErro(err.message);
      } finally {
        setCarregando(false);
      }
    })();
  }, [jobId]);

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      const res = await fetch(`${API_URL}/admin/jobs/${jobId}`, { method: 'PATCH', headers: hdr(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || 'Erro ao salvar');
      onSalvo();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', marginBottom: 10, borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13 };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, padding: 28, width: 460, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: V.text }}>Editar vaga externa</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: V.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        {erro && <div style={{ background: '#3a1a1a', border: '1px solid #7a2e2e', color: '#f0a0a0', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{erro}</div>}

        {carregando || !form ? (
          <div style={{ color: V.muted }}>Carregando...</div>
        ) : (
          <>
            <input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={inputStyle} />
            <input placeholder="Empresa" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} style={inputStyle} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input placeholder="Cidade" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} style={inputStyle} />
              <input placeholder="UF" maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} style={{ ...inputStyle, width: 70 }} />
            </div>
            <textarea placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} style={inputStyle} />
            <input placeholder="Salário (opcional)" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} style={inputStyle} />
            <input placeholder="Link do site da empresa / anúncio original" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} style={inputStyle} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: V.muted, fontSize: 13, marginBottom: 10 }}>
              <input type="checkbox" checked={form.remote} onChange={(e) => setForm({ ...form, remote: e.target.checked })} />
              Remoto
            </label>

            <button onClick={salvar} disabled={salvando}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {salvando ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Modal: importar vagas via CSV ──────────────────────────────
function ModalImportarVagasCsv({ onClose, onImportado }) {
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState(null);

  const enviar = async () => {
    if (!arquivo) { setErro('Selecione um arquivo CSV'); return; }
    setEnviando(true);
    setErro('');
    try {
      const formData = new FormData();
      formData.append('file', arquivo);
      const res = await fetch(`${API_URL}/admin/vagas/importar-csv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok()}` }, // sem Content-Type: o browser define o boundary do multipart
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || 'Erro ao importar CSV');
      setResultado(data);
    } catch (err) {
      setErro(err.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, padding: 28, width: 480, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: V.text }}>Importar vagas via CSV</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: V.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        {erro && <div style={{ background: '#3a1a1a', border: '1px solid #7a2e2e', color: '#f0a0a0', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{erro}</div>}

        {resultado ? (
          <>
            <div style={{ background: V.surface2, borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: V.text }}>
              {resultado.inseridas} de {resultado.total} vaga(s) importada(s).
              {resultado.erros?.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ color: V.amber, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{resultado.erros.length} erro(s):</div>
                  <div style={{ maxHeight: 140, overflowY: 'auto', fontSize: 11, color: V.muted2 }}>
                    {resultado.erros.map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                </div>
              )}
            </div>
            <button onClick={onImportado}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Concluir
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: V.muted, marginBottom: 14, lineHeight: 1.6 }}>
              Colunas do CSV: <code>titulo</code> (obrigatório), <code>empresa_email</code> (se a empresa já
              tem conta) ou <code>empresa_nome</code> (se ainda não tem — fica reivindicável depois),
              <code> descricao</code>, <code>cidade</code>, <code>estado</code>, <code>tipo_contrato</code>,{' '}
              <code>modalidade</code>, <code>area</code>, <code>salario</code>, <code>requisitos</code>.
              Máximo 500 linhas.
            </div>
            <input type="file" accept=".csv"
              onChange={(e) => setArquivo(e.target.files?.[0] || null)}
              style={{ width: '100%', marginBottom: 16, color: V.text, fontSize: 13 }} />
            <button onClick={enviar} disabled={enviando}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {enviando ? 'Importando...' : 'Importar'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── CRM comercial ───────────────────────────────────────────────
const ESTAGIOS_CRM = [
  { key: 'lead_identificado', label: 'Lead identificado' },
  { key: 'contato_realizado', label: 'Contato realizado' },
  { key: 'demonstracao', label: 'Demonstração' },
  { key: 'teste', label: 'Teste' },
  { key: 'proposta_enviada', label: 'Proposta enviada' },
  { key: 'negociacao', label: 'Negociação' },
  { key: 'cliente_ativo', label: 'Cliente ativo' },
  { key: 'cliente_perdido', label: 'Cliente perdido' },
];

function CRMComercial() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNovo, setModalNovo] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/crm/leads`, { headers: hdr() });
      const data = await res.json();
      setLeads(data.leads || []);
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const moverEstagio = async (id, estagio_comercial) => {
    try {
      await fetch(`${API_URL}/admin/crm/leads/${id}`, { method: 'PATCH', headers: hdr(), body: JSON.stringify({ estagio_comercial }) });
      carregar();
    } catch { /* noop */ }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: V.text, margin: 0 }}>CRM comercial</h2>
        <button onClick={() => setModalNovo(true)}
          style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          + Novo lead
        </button>
      </div>

      {loading ? (
        <div style={{ color: V.muted }}>Carregando...</div>
      ) : (
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
          {ESTAGIOS_CRM.map((estagio) => {
            const leadsDoEstagio = leads.filter((l) => (l.estagio_comercial || 'lead_identificado') === estagio.key);
            return (
              <div key={estagio.key} style={{ minWidth: 220, flex: '0 0 220px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: V.muted, textTransform: 'uppercase', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{estagio.label}</span>
                  <span style={{ color: V.muted2 }}>{leadsDoEstagio.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {leadsDoEstagio.map((lead) => (
                    <div key={lead.id} style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ color: V.text, fontSize: 13, fontWeight: 600 }}>{lead.nome}</div>
                      <div style={{ color: V.muted2, fontSize: 11, marginTop: 2 }}>{lead.cidade}{lead.setor ? ` · ${lead.setor}` : ''}</div>
                      {lead.responsavel && <div style={{ color: V.muted2, fontSize: 11 }}>Resp.: {lead.responsavel}</div>}
                      <select
                        value={estagio.key}
                        onChange={(e) => moverEstagio(lead.id, e.target.value)}
                        style={{ width: '100%', marginTop: 8, padding: '6px 8px', borderRadius: 6, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 11 }}>
                        {ESTAGIOS_CRM.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalNovo && <ModalNovoLead onClose={() => setModalNovo(false)} onCriado={() => { setModalNovo(false); carregar(); }} />}
    </div>
  );
}

function ModalNovoLead({ onClose, onCriado }) {
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState({
    nome: '', setor: '', cidade: '', estado: 'RS', responsavel: '',
    telefone: '', telefone_whatsapp: '', email_rh: '', origem: 'indicacao',
  });
  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', marginBottom: 10, borderRadius: 8, border: `1px solid ${V.border}`, background: V.surface2, color: V.text, fontSize: 13 };

  const salvar = async () => {
    if (!form.nome) { setErro('Nome da empresa é obrigatório'); return; }
    setSalvando(true);
    setErro('');
    try {
      const res = await fetch(`${API_URL}/admin/crm/leads`, { method: 'POST', headers: hdr(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao cadastrar lead');
      onCriado();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, padding: 28, width: 420, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: V.text }}>Novo lead</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: V.muted, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {erro && <div style={{ background: '#3a1a1a', border: '1px solid #7a2e2e', color: '#f0a0a0', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{erro}</div>}

        <input placeholder="Empresa" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} style={inputStyle} />
        <input placeholder="Segmento" value={form.setor} onChange={(e) => setForm({ ...form, setor: e.target.value })} style={inputStyle} />
        <div style={{ display: 'flex', gap: 8 }}>
          <input placeholder="Cidade" value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} style={inputStyle} />
          <input placeholder="UF" maxLength={2} value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase() })} style={{ ...inputStyle, width: 70 }} />
        </div>
        <input placeholder="Responsável" value={form.responsavel} onChange={(e) => setForm({ ...form, responsavel: e.target.value })} style={inputStyle} />
        <input placeholder="Telefone" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} style={inputStyle} />
        <input placeholder="WhatsApp" value={form.telefone_whatsapp} onChange={(e) => setForm({ ...form, telefone_whatsapp: e.target.value })} style={inputStyle} />
        <input placeholder="E-mail" type="email" value={form.email_rh} onChange={(e) => setForm({ ...form, email_rh: e.target.value })} style={inputStyle} />
        <select value={form.origem} onChange={(e) => setForm({ ...form, origem: e.target.value })} style={inputStyle}>
          {['indicacao', 'inbound', 'outbound', 'evento', 'google_maps', 'planilha'].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>

        <button onClick={salvar} disabled={salvando}
          style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: V.accent2, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {salvando ? 'Cadastrando...' : 'Cadastrar lead'}
        </button>
      </div>
    </div>
  );
}

// ─── Shell principal ────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [aba, setAba] = useState('visao-geral');
  const [checando, setChecando] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login', { replace: true });
    } else {
      setChecando(false);
    }
  }, [navigate]);

  const sair = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login', { replace: true });
  };

  if (checando) return null;

  const admin = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const NAV = [
    { key: 'visao-geral', label: 'Visão geral' },
    { key: 'empresas', label: 'Empresas' },
    { key: 'vagas', label: 'Vagas' },
    { key: 'scrapers', label: 'Scrapers' },
    { key: 'crm', label: 'CRM comercial' },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: V.bg, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <aside style={{ width: 220, borderRight: `1px solid ${V.border}`, padding: '24px 16px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: V.orange, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 24 }}>
          Conecta Lagoa
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
          {NAV.map((item) => (
            <button key={item.key} onClick={() => setAba(item.key)}
              style={{
                textAlign: 'left', padding: '10px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
                background: aba === item.key ? V.accent : 'transparent',
                color: aba === item.key ? 'white' : V.muted, fontWeight: aba === item.key ? 600 : 400,
              }}>
              {item.label}
            </button>
          ))}
        </nav>
        <div style={{ borderTop: `1px solid ${V.border}`, paddingTop: 16 }}>
          <div style={{ fontSize: 12, color: V.muted, marginBottom: 10 }}>{admin.nome || admin.email}</div>
          <button onClick={sair} style={{ width: '100%', padding: '8px', borderRadius: 8, border: `1px solid ${V.border}`, background: 'none', color: V.muted, fontSize: 12, cursor: 'pointer' }}>
            Sair
          </button>
        </div>
      </aside>

      <main style={{ flex: 1, padding: '32px 40px', overflowY: 'auto' }}>
        {aba === 'visao-geral' && <VisaoGeral />}
        {aba === 'empresas' && <GestaoEmpresas />}
        {aba === 'vagas' && <GestaoVagas />}
        {aba === 'scrapers' && <PainelScrapers />}
        {aba === 'crm' && <CRMComercial />}
      </main>
    </div>
  );
}
